import { PersistedKey } from '../actions/hydration.actions';

export type StringSanitizeRule = { type: 'string'; default?: string | null };
export type EnumSanitizeRule = { type: 'enum'; allowed: readonly unknown[]; default?: unknown };
export type IdsSanitizeRule = { type: 'ids'; available: readonly string[]; default?: readonly string[] | null };
export type LeafSanitizeRule = StringSanitizeRule | EnumSanitizeRule | IdsSanitizeRule;

export type NestedSanitizeRules = {
  [key: string]: LeafSanitizeRule;
};

export type ObjectSanitizeRule<T extends object = Record<string, unknown>> = {
  type: 'object';
  rules: SanitizeRules<T>;
  default?: T | null;
};

export type SanitizeRule = LeafSanitizeRule | ObjectSanitizeRule;

export type SanitizeRules<T extends object> = {
  [K in keyof T]?: SanitizeRule | NestedSanitizeRules;
};

export function getHydrationStorageKey(element: PersistedKey, userId: string): string {
  return `${element.key.join('.')}.${userId}`;
}

export function readPersistedSlice<T extends object>(element: PersistedKey, userId: string): Partial<T> | null {
  const storageKey = getHydrationStorageKey(element, userId);
  const storageValue = localStorage.getItem(storageKey);

  if (!storageValue) {
    return null;
  }

  try {
    const parsed: unknown = JSON.parse(storageValue);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return null;
    }

    return parsed as Partial<T>;
  } catch {
    localStorage.removeItem(storageKey);
    return null;
  }
}

export function sanitizeListParams<T extends object>(persisted: Partial<T> | null | undefined, rules: SanitizeRules<T>): T;
export function sanitizeListParams<T extends object>(
  persisted: Partial<T> | null | undefined,
  defaults: T,
  rules: SanitizeRules<T>
): T;
export function sanitizeListParams<T extends object>(
  persisted: Partial<T> | null | undefined,
  defaultsOrRules: T | SanitizeRules<T>,
  maybeRules?: SanitizeRules<T>
): T {
  const rules = (maybeRules ?? defaultsOrRules) as SanitizeRules<T>;
  const defaults = (maybeRules ? defaultsOrRules : defaultsFromRules(rules)) as T;

  return omitEmptyValues(applySanitizeRules(persisted, defaults, rules));
}

export function omitEmptyValues<T extends object>(value: T): T {
  return (Object.keys(value) as Extract<keyof T, string>[]).reduce((acc, key) => {
    const current = value[key];
    const next = isRecord(current) ? omitEmptyValues(current) : current;

    return isEmptyValue(next) ? acc : { ...acc, [key]: next };
  }, {} as T);
}

function applySanitizeRules<T extends object>(persisted: Partial<T> | null | undefined, defaults: T, rules: SanitizeRules<T>): T {
  const result = { ...defaults };

  if (!persisted) {
    return result;
  }

  (Object.keys(rules) as Extract<keyof T, string>[]).forEach((key) => {
    const rule = rules[key];
    const value = persisted[key];

    if (!rule) {
      return;
    }

    const nestedRules = nestedRulesFrom(rule);
    if (nestedRules) {
      const nestedPersisted = isRecord(value) ? value : null;
      const nestedDefaults = isRecord(result[key]) ? result[key] : defaultsFromRules(nestedRules);
      result[key] = applySanitizeRules(nestedPersisted, nestedDefaults, nestedRules) as T[typeof key];
      return;
    }

    if (!isLeafRule(rule) || value === undefined || value === null) {
      return;
    }

    switch (rule.type) {
      case 'string': {
        if (typeof value === 'string') {
          result[key] = value as T[typeof key];
        }
        break;
      }
      case 'enum': {
        if (rule.allowed.includes(value)) {
          result[key] = value as T[typeof key];
        }
        break;
      }
      case 'ids': {
        if (Array.isArray(value)) {
          result[key] = value.filter((id): id is string => typeof id === 'string' && rule.available.includes(id)) as T[typeof key];
        }
        break;
      }
    }
  });

  return result;
}

function defaultsFromRules<T extends object>(rules: SanitizeRules<T>): T {
  const defaults = {} as T;

  (Object.keys(rules) as Extract<keyof T, string>[]).forEach((key) => {
    const rule = rules[key];
    if (!rule) {
      return;
    }

    if (isObjectRule(rule)) {
      defaults[key] = (rule.default !== undefined ? rule.default : defaultsFromRules(rule.rules)) as T[typeof key];
      return;
    }

    if (isNestedRules(rule)) {
      defaults[key] = defaultsFromRules(rule) as T[typeof key];
      return;
    }

    if (isLeafRule(rule)) {
      defaults[key] = (rule.default !== undefined ? rule.default : null) as T[typeof key];
    }
  });

  return defaults;
}

function nestedRulesFrom(rule: SanitizeRule | NestedSanitizeRules): SanitizeRules<Record<string, unknown>> | null {
  if (isObjectRule(rule)) {
    return rule.rules;
  }

  if (isNestedRules(rule)) {
    return rule;
  }

  return null;
}

function isLeafRule(value: unknown): value is LeafSanitizeRule {
  if (!isRecord(value)) {
    return false;
  }

  const type = value['type'];
  return type === 'string' || type === 'enum' || type === 'ids';
}

function isObjectRule(value: unknown): value is ObjectSanitizeRule {
  return isRecord(value) && value['type'] === 'object' && isRecord(value['rules']);
}

function isNestedRules(value: unknown): value is NestedSanitizeRules {
  if (!isRecord(value) || isLeafRule(value) || isObjectRule(value)) {
    return false;
  }

  const values = Object.values(value);
  return values.length > 0 && values.every((item) => isLeafRule(item));
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

function isEmptyValue(value: unknown): boolean {
  if (value == null) {
    return true;
  }

  if (typeof value === 'string' || Array.isArray(value)) {
    return value.length === 0;
  }

  if (isRecord(value)) {
    return Object.keys(value).length === 0;
  }

  return false;
}
